import React from "react";
import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";

/**
 * 모달의 닫힘 규칙. push 할 때 넘기고 ModalStack 이 읽는다 — 이 두 이름이 닫힘 차단의
 * 전부이므로, 양쪽이 같은 타입을 보게 해서 이름이 어긋나면 컴파일이 깨지게 한다.
 * (조용히 undefined 가 되면 업로드 중 모달이 닫혀 완료 처리가 유실된다.)
 * 생략 = 허용(Radix 기본값).
 */
export interface ModalCloseOptions {
  /** dim(모달 바깥) 클릭으로 닫기 */
  closeOnPointerDownOutside?: boolean;
  /** ESC 로 닫기 */
  closeOnEscapeKey?: boolean;
}

export interface ModalInstance<P = {}> {
  id: string;
  Component: React.ComponentType<P>;
  props: P;
  /** push 된 시점의 화면(pathname). 이탈 정리가 "어느 화면의 모달인지" 가르는 기준. */
  pathname: string;
}

/**
 * 현재 화면의 pathname. App 이 **layout effect 에서** 알려준다(usePageModalCleanup) —
 * 자식의 `useEffect` push 보다 반드시 먼저여야 한다. 자세한 순서 계약은 그 훅 참고.
 */
let currentPathname = "";

export function setModalPathname(pathname: string) {
  currentPathname = pathname;
}

interface ModalStore {
  isAddTagModalOpen: boolean;
  isParentContextModalOpen: boolean;
  isTagDetailModalOpen: boolean;

  setAddTagModalOpen: (isOpen: boolean) => void;
  setParentContextModalOpen: (isOpen: boolean) => void;
  setTagDetailModalOpen: (isOpen: boolean) => void;

  modals: {
    stack: ModalInstance<any>[];
    push: <P>(
      Component: React.ComponentType<P>,
      props?: P & { id?: string } & ModalCloseOptions,
    ) => string;
    pop: (options?: { callbacks?: (() => void)[] }) => void;
    remove: (id: string) => void;
    clear: () => void;
    exists: (id: string) => boolean;
  };

  closeAllModals: () => void;
}

export const useModalStore = create<ModalStore>()(
  subscribeWithSelector((set, get) => ({
    isAddTagModalOpen: false,
    isParentContextModalOpen: false,
    isTagDetailModalOpen: false,

    setAddTagModalOpen: (isOpen) => set({ isAddTagModalOpen: isOpen }),
    setParentContextModalOpen: (isOpen) =>
      set({ isParentContextModalOpen: isOpen }),
    setTagDetailModalOpen: (isOpen) => set({ isTagDetailModalOpen: isOpen }),

    modals: {
      stack: [] as ModalInstance<any>[],
      push: <P>(
        Component: React.ComponentType<P>,
        props?: P & { id?: string },
      ) => {
        const modalId = props?.id || Date.now().toString();
        set((state) => ({
          modals: {
            ...state.modals,
            stack: [
              ...state.modals.stack,
              { id: modalId, Component, props, pathname: currentPathname },
            ],
          },
        }));
        return modalId;
      },
      exists: (id: string): boolean => {
        const stack = get().modals.stack;
        if (!stack || stack.length === 0) return false;
        return stack.some((modal) => modal.id === id);
      },
      pop: ({ callbacks }: { callbacks?: (() => void)[] } = {}) => {
        callbacks?.forEach((callback) => callback?.());
        set((state) => ({
          modals: {
            ...state.modals,
            stack: state.modals.stack.slice(0, -1),
          },
        }));
      },
      /** id 로 지정해 닫는다. pop 과 달리 최상단이 아니어도 그 모달만 제거한다. */
      remove: (id: string) =>
        set((state) => ({
          modals: {
            ...state.modals,
            stack: state.modals.stack.filter((modal) => modal.id !== id),
          },
        })),
      clear: () => set((state) => ({ modals: { ...state.modals, stack: [] } })),
    },

    closeAllModals: () => {
      set((state) => ({
        isAddTagModalOpen: false,
        isParentContextModalOpen: false,
        isTagDetailModalOpen: false,
        modals: { ...state.modals, stack: [] },
      }));
    },
  })),
);

export function pushModal<P>(
  Component: React.ComponentType<P>,
  props?: P & { id?: string } & ModalCloseOptions,
) {
  useModalStore.getState().modals.push(Component, props);
}

export function popModal() {
  useModalStore.getState().modals.pop();
}

export function removeModal(id: string) {
  useModalStore.getState().modals.remove(id);
}

export function existsModal(id: string) {
  return useModalStore.getState().modals.exists(id);
}

/**
 * 라우트가 바뀌면(페이지 이탈) 그 페이지가 띄운 모달을 닫는다. ModalStack 은 라우트 바깥
 * 앱 루트에서 렌더되므로 화면이 언마운트돼도 모달과 dim 이 남아, 옮겨 간 화면이 클릭되지
 * 않는다(WXBRL QA: 워크북 값 불러오기 모달).
 *
 * 남기는 것은 두 가지다.
 * 1. 현재 화면에서 떠오른 모달 — 새 화면이 진입하며 띄우는 모달(버전 포워드 "변경 사항
 *    확인")도 push 시점에 새 pathname 이 찍혀 여기서 살아남는다(순서 계약은
 *    usePageModalCleanup).
 * 2. ESC·dim 을 **둘 다** 막아 둔 모달 — 그 조합은 "끊기면 완료 처리가 유실되는 작업 중"
 *    이라는 뜻이다(업로드 진행 모달). 한쪽만 막은 모달은 그냥 닫는다.
 *
 * ponytail: 보존 여부를 새 플래그로 받지 않고 기존 닫힘 옵션을 재사용한다 —
 * 이 기준으로 가를 수 없는 모달이 생기면 그때 전용 플래그를 넘긴다.
 */
export function closePageModals() {
  useModalStore.setState((state) => ({
    modals: {
      ...state.modals,
      stack: state.modals.stack.filter((modal) => {
        if (modal.pathname === currentPathname) return true;
        const options: ModalCloseOptions = modal.props ?? {};
        return (
          options.closeOnEscapeKey === false &&
          options.closeOnPointerDownOutside === false
        );
      }),
    },
  }));
}
